import {
  ApiError,
  type CallbackCallResponse,
  queryKeys,
  type VoiceCallOption,
} from '@mevaser/shared'
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  useToast,
} from '@mevaser/ui'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Phone, Tablet } from 'lucide-react'
import { api } from '../../lib/api.js'
import { useAuth } from '../../lib/auth.js'
import { useSoftphone } from './softphone-provider.js'

/**
 * A click-to-call icon button for wherever a customer's number is shown
 * (t0869) — a phone row on the customer page, a ticket header. Renders
 * nothing when browser calling isn't available, or a call is already under
 * way (only one call at a time).
 *
 * When at least one linked phone is configured (t0887, the same ring-list
 * rows Support Settings manages for inbound calls), this becomes a dropdown
 * offering a choice of route: the browser softphone, unchanged and still the
 * plain default, or one of the linked phones — which rings that phone first
 * and only dials the customer once staff answers.
 */
export function CallButton({ phone, label }: { phone: string; label?: string }) {
  const { available, state, placeCall, otherDevices, placeCallOnDevice } = useSoftphone()
  const { staff } = useAuth()
  const toast = useToast()

  const { data: options } = useQuery({
    queryKey: queryKeys.supportVoiceCallOptions(),
    queryFn: () => api.get<VoiceCallOption[]>('/support/voice/call-options'),
    enabled: available,
    staleTime: 5 * 60_000,
  })

  const callback = useMutation({
    mutationFn: (option: VoiceCallOption) =>
      api.post<CallbackCallResponse>('/support/voice/callback-calls', {
        to: phone,
        callTargetId: option.id,
      }),
    onSuccess: (_data, option) => toast.success(`Ringing ${option.label}…`),
    onError: (error) =>
      toast.error(
        'Could not start the call',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  const ringDevice = useMutation({
    mutationFn: (device: { deviceKey: string; label: string }) =>
      placeCallOnDevice(phone, device.deviceKey),
    onSuccess: (_data, device) => toast.success(`Ringing ${device.label}…`, 'Answer it there.'),
    onError: (error) =>
      toast.error(
        'Could not start the call',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  if (!available) return null

  const disabled = state !== 'idle' || callback.isPending || ringDevice.isPending
  const callInBrowser = () => {
    void placeCall(phone).catch(() => toast.error('Could not place the call'))
  }

  if ((!options || options.length === 0) && otherDevices.length === 0) {
    return (
      <Button
        variant="ghost"
        size={label ? 'sm' : 'icon'}
        aria-label={label ? undefined : `Call ${phone}`}
        disabled={disabled}
        onClick={callInBrowser}
      >
        <Phone className="size-3.5" aria-hidden="true" />
        {label}
      </Button>
    )
  }

  // A target with `staffId` matching the signed-in staff member is their own
  // linked phone — float it to the top of the list, ahead of the server's
  // priority order used for inbound ringing. `sort` is stable, so targets
  // tie-break back to that priority order.
  const isMine = (option: VoiceCallOption) => option.staffId === staff?.id
  const sorted = [...(options ?? [])].sort((a, b) => Number(isMine(b)) - Number(isMine(a)))

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size={label ? 'sm' : 'icon'}
          aria-label={label ? undefined : `Call ${phone}`}
          disabled={disabled}
        >
          <Phone className="size-3.5" aria-hidden="true" />
          {label}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={callInBrowser}>
          <Phone /> Call in browser
        </DropdownMenuItem>
        {otherDevices.length > 0 ? <DropdownMenuSeparator /> : null}
        {otherDevices.map((device) => (
          <DropdownMenuItem
            key={device.deviceKey}
            disabled={ringDevice.isPending}
            onSelect={() => ringDevice.mutate(device)}
          >
            <Tablet /> Ring {device.label}
          </DropdownMenuItem>
        ))}
        {sorted.length > 0 ? <DropdownMenuSeparator /> : null}
        {sorted.map((option) => (
          <DropdownMenuItem
            key={option.id}
            disabled={callback.isPending}
            onSelect={() => callback.mutate(option)}
          >
            <Phone /> {option.label}
            {option.staffId === staff?.id ? ' (yours)' : ''}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
